//Componentes generales
import { useContext, useEffect, useMemo, useState } from 'react';
import clienteAxios from '../../config/axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import Select from 'react-select';

//Estilos
import './FormUser.css';
import { ICONS1, PRINCIPAL, NAVBARS, COLOR_BOTONES } from '../../constants/colors';

// Alertas
import Swal from 'sweetalert2'
import { flexbox } from '@mui/system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

import activoContext from '../../context/activoContex/activoContex.js';
import usuariosContext from '../../context/usuarios/usuariosContext.js';
import checklistContext from '../../context/checklistContext/checklistContext.js';
import nivel1Context from '../../context/nivel1Context/nivel1Context.js';
import DictionaryText from '../dictionaryText/DictionaryText.js';

/*
    Componente encargado de mostrar el formulario de usuario.
    Donde data es el usuario crear/modificar.
    minimizar es la función encargada de ocultar el dialogo.
    generar es la función del context que guarda el usuario.
    titulo es la que determina que texto mostrar en encabezado del dialogo.
    verficaUser es la función del context que comprueba la licencia por usuario vigente del cliente.
*/
function FormUser({ data = undefined, minimizar, generar, titulo, verificaUser, listGroups, uSession }) {

    const {activoList} = useContext(activoContext);
    const {checklistList} = useContext(checklistContext);
    const {userList, getActivosAllowedByUserID, getChecklistsAllowedByUserID} = useContext(usuariosContext);
    const {nivel1Lista} = useContext(nivel1Context);

    const areasOptions = useMemo(()=>{
            if(nivel1Lista && nivel1Lista.length > 0) return nivel1Lista.map(a => ({label: a.name, value: a._id}));
            else return [];
    },[nivel1Lista])

    const userOptions = useMemo(() => {
        if(userList){
            let userListFiltered = data ? userList.filter(u => u.userName != data.userName) : userList; 
            return userListFiltered ? userListFiltered.map(u => ({ label: u.fullName, value: u._id})) : [];
        }else return [];
    }, [userList]);

    const checklistsOptions = useMemo(()=>{
        if(checklistList){
            return checklistList.map(c => ({value: c._id, label: c.name}));
        }else return [];
    },[checklistList])

    //Asignar activos se permite a cualquier usuario menos al raiz, el username de este esta conformado por la palabra Admin y el Alias de la Empresa.
    const showActivosAllowedFilter = (!data || data.userName != `Admin${data.tenantId}`);
    //Se usa tanto para Activos como para Checklists.

    //State de usuario. Si no viene data por parametro significa que es nuevo por lo que se le asigna una estructura.
    const [user, setUser] = useState((data !== undefined) ? {...data, password: ''} : {
        userName: '',
        fullName: '',
        shortName: '',
        password: '',
        email: '',
        enabled: (false),
        userType: [{ _id: '', typeName: '', descr: '' }],
        gruposV: [],
        activosAllowed: '',
        checklistsAllowed: [],
        nroSocio: ''
    });
    const [showPassword, setShowPassword] = useState(false);

    //State para errores. Valida que los datos ingresados por el usuario sean correctos.
    const [error, setError] = useState({
        userName: false,
        fullName: false,
        shortName: false,
        email: false,
        password: false,
        userType: false,
        socioNoNro: false,
        msg: ''
    });

    //Constante que define el titulo que corresponde si vienen datos por parametros
    const tituloBoton = data === undefined ? 'Agregar' : 'Modificar';
    //State para items. En este contexto, llamamos items a los roles que puede poseer un usuario.
    const [items, setItems] = useState([]);
    //State para grupos. En este contexto, llamamos grupos a los grupos que puede poseer un usuario.
    const [groups, setGroups] = useState([]);
    //State que determina si el usuario tiene licencias.
    const [controlUsuarios, setControlUsuarios] = useState(false);
    //State que determina el rol del usuario seleccionado.
    const [selectedOptions, setSelectedOptions] = useState(user.userType?.length > 0 && user.userType[0]._id !== '' ? user.userType.map((item) => ({value: item.typeName, label: item.descr})) : []);
    //State que determina los grupos que visualiza el usuario seleccionado.
    const [selectedGroupOptions, setGroupSelectedOptions] = useState([]);
    //State que determina los activos que visualiza el usuario seleccionado.
    const [selectedActivosOptions, setActivosSelectedOptions] = useState(data?.activosAllowed ?? []);
    const [selectedChecklistsOptions, setChecklistsSelectedOptions] = useState(data?.checklistsAllowed ?? []);

    const esSocio = selectedOptions?.some(opt => opt.value === 'SOCIO');
      
    /*
        Función que muestra un pop it alerta de tipo advertencia.
            Donde mensaje es lo que queremos que diga la alerta.
    */
    const alerta = (mensaje) => {
        Swal.fire({
            text: mensaje, 
            icon: 'warning',
            confirmButtonText: 'OK'
        })
    };

    /* 
        Manejador de selección de roles de usuario.
        Donde selected son todos los roles seleccionado. 
    */
    const handleSelectChange = (selected) => {
        setSelectedOptions(selected);
        const filteredArray = items.filter(obj => selected.some(sel => obj.typeName === sel.value));
        setUser({ ...user, userType: filteredArray});
    };
    
    /* 
        Manejador de activos que tiene permitido el usuario visualizar.
    */
    const handleGetActivosAllowedFromUser = async(selected) => {
        const resp = await getActivosAllowedByUserID(selected.value);
        setActivosSelectedOptions(() => {
            if(selectedActivosOptions){
                let prevActivos = selectedActivosOptions.slice();
                let newActivos = [...prevActivos, ...resp.filter(a => !prevActivos.some(prevA => prevA.value == a.value))]
                return newActivos;
            }else return resp;
        });
    };
    
    const handleChangeFacilitatorNivel1 = async (value) => {
        let activosFiltered = activoList.filter(a => a.activoNivel1ID == value.value);
        activosFiltered = activosFiltered.map(a => ({label: a.name, value: a.codActivo}));
        setActivosSelectedOptions(() => {
            if(selectedActivosOptions){
                let prevActivos = selectedActivosOptions.slice();
                let newActivos = [...prevActivos, ...activosFiltered.filter(a => !prevActivos.some(prevA => prevA.value == a.value))]
                return newActivos;
            }else return activosFiltered;
        });
    }
    
    const handleGetChecklistsAllowedFromUser = async(selected) => {
        const resp = await getChecklistsAllowedByUserID(selected.value);
        setChecklistsSelectedOptions(() => {
            if(selectedChecklistsOptions){
                let prevChecklists = selectedChecklistsOptions.slice();
                let newChecklists = [...prevChecklists, ...resp.filter(a => !prevChecklists.some(prevA => prevA.value == a.value))]
                return newChecklists;
            }else return resp;
        });
    };
    
    /* 
        Manejador de selección de roles de usuario.
            Donde selected son todos los roles seleccionado. 
    */
    const handleChangeActivosAllowed = (selected) => {
        setActivosSelectedOptions(selected);
        setUser({ ...user, activosAllowed: selected});
    };
    const handleChangeChecklistsAllowed = (selected) => {
        setChecklistsSelectedOptions(selected);
        setUser({ ...user, checklistsAllowed: selected});
    };

    /* 
        Manejador de selección de grupos a visualizar del usuario.
            Donde selected son todos los grupos seleccionado. 
    */
    const handleSelectGroupChange = (selected) => {
        setGroupSelectedOptions(selected.map((i) => ({ _id: i.value, name: i.label })));
        const filteredArray = selected.map((i) => i.value);
        setUser({ ...user, gruposV: filteredArray});
    };

    /*
        Guarda el usuario mediante función del context.
        Antes de llamar a la función, verifica que pase todas las validaciones.
    */
    const handlerGenerar = async () => {

        let userModelated = {...user};
        userModelated.activosAllowed = selectedActivosOptions;
        userModelated.checklistsAllowed = selectedChecklistsOptions.map(c => c.value);

        const verificar = async () => {
            let isGenerable = true;
            let errors = {
                userName: false,
                fullName: false,
                shortName: false,
                email: false,
                password: false,
                userType: [],
                socioNoNro: false,
                msg: ''
            };

            //Verificaciones
            if (data === undefined) {
                if (userModelated.userName === '') {
                    errors.userName = true
                    errors.msg = 'No ingreso un nombre de usuario';
                } else {
                    try {
                        const resp = await clienteAxios.get(`/user/userName/${userModelated.userName}`);
                        
                        if(resp.data.data !== null) {
                            errors.userName = true;
                            errors.msg = 'Ingreso un nombre de usuario ya existente';
                        }    
                    } catch (error) {
                        
                    }

                };
            }
            if (userModelated.fullName === '') {
                errors.fullName = true;
                errors.msg = 'No ingreso un nombre';
            }
            if (!userModelated.shortName || userModelated.shortName.length < 4) {
                errors.shortName = true;
                errors.msg = 'El nombre abreviado debe tener al menos 4 caracteres';
            }

            if (userModelated.email === '') {
                errors.email = true;
                errors.msg = 'No ingreso un email';
            }
            if (userModelated.password === '' && data === undefined) {
                errors.password = true;
                errors.msg = 'No ingreso una contraseña';
            }
            if (userModelated.userType.length < 1) {
                errors.userType = true;
                errors.msg = 'Tipo de usuario no valido';
            }

            if(userModelated.userType.some(uT => uT.descr == 'SOCIO') && !userModelated.nroSocio){
                errors.socioNoNro = true;
                errors.msg = 'Debe completar el número de socio para poder guardar';
            }
            
            setError(errors);

            if (errors.userName === true || errors.fullName === true || errors.shortName === true || errors.email === true || errors.password === true || errors.userType === true || errors.socioNoNro === true) isGenerable = false;

            return isGenerable;
        };

        const resp = await verificar();

        if (resp === true) generar(userModelated, (data === undefined) ? 0 : 1);

        setActivosSelectedOptions([]);
    }
    
    const handleChangeOptions = (e) => {
        let {name,checked} = e.target;
        setUser({...user,[name]:checked});
    }

    const generarContrasena = () => {
        const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const lower = 'abcdefghijklmnopqrstuvwxyz';
        const digits = '0123456789';
        let password = '';
        for (let i = 0; i < 6; i++) {
            password += digits[Math.floor(Math.random() * digits.length)];
        }
        password += upper[Math.floor(Math.random() * upper.length)];
        password += lower[Math.floor(Math.random() * lower.length)];
        setUser({ ...user, password });
    }

    /* 
        Al renderizar el componente, setea los roles disponibles.
        setea la posibilidad de habilitar el usuario segun las licencias que posee. Si tiene usadas todas sus licencias, permite crear el usuario mas no permite habilitarlo.
    */
    useEffect(() => {

        //Actualiza la Lista de roles disponibles.
        const actualizar = async () => {
            const resp = await clienteAxios.get(`/userType`);
            let items = new Array();

            resp.data.data.map((type) => {
                items.push({ _id: type._id, typeName: type.typeName, descr: type.descr })
            });

            if(user.userName !== ''){
                const resp2 = await clienteAxios.get(`/grupoUsuarios/complete?guUserID=${user._id}`);
                let items2 = new Array();
                
                (resp2 != undefined && resp2.data.data.length > 0) && resp2.data.data.map((type) => {
                    items2.push({ _id: type.grupo._id, name: type.grupo.name })
                });

                //Recorrer groupV recuperar de cada grupo en ListGroup el objeto completo y luego crear el arreglo options
                if(user.gruposV && listGroups){
                    const listGroupsUser = user.gruposV.map((idGroup) => {
                        const group = listGroups.find((lg) => lg._id === idGroup);
                        return group;
                    }); 
        
                    const items3 = (listGroupsUser != undefined && listGroupsUser.length > 0 && listGroupsUser[0] != undefined) ? listGroupsUser.map((g) => ({ _id: g._id, name: g.name })) : []

                    //Setea Visualiza de
                    setGroupSelectedOptions(items3);
                }
                setGroups(items2);
            }
            setItems(items);
        }
        actualizar();

        //Recupera usuarios habilitados y seteo state de control.
        const verificar = async () => {
            const licenciaUsuario = await verificaUser();
            // RESTRICCIÓN TEMPORALMENTE DESHABILITADA
            // if(user.enabled){
            //     setControlUsuarios(false);
            // }else{
            //     if(licenciaUsuario.data.userEnabled === licenciaUsuario.data.maxUsersEnabled){
            //         setControlUsuarios(true);
            //     }else{
            //         setControlUsuarios(false);
            //     }
            // }
            setControlUsuarios(false); // Siempre permitir habilitar usuarios
        }
        verificar();

    }, []);

    /* 
        Disparador ante cambios en el state de error. Si alguno de sus atributos es true, muestra alerta.
    */
    useEffect(() => {
        if (error.userName || error.fullName || error.shortName || error.email || error.password || error.userType.lenght > 0 || error.socioNoNro) alerta(error.msg);
    }, [error])

    useEffect(()=>{
        if(listGroups && listGroups.length > 0){
            let everyoneGroup = listGroups.find(lG => lG.name == 'Everyone');
            if(everyoneGroup){
                //Si everyone no esta presente en los grupos que ya visualiza el usuario, se lo asigna.
                if(!user.gruposV.some(gV => gV == everyoneGroup._id)){
                    console.log("No visualizaba everyone, ahora seteado");
                    setGroupSelectedOptions([{label: everyoneGroup.name, value:everyoneGroup._id}]);
                    setUser({...user, ['gruposV']: [...user.gruposV.slice(),everyoneGroup._id]});
                }
            }
        }
        
    },[listGroups])
    
    return (
        <div className='container-gr-form'>
            <div className='title-form' style={{backgroundColor: PRINCIPAL , color:NAVBARS}}>
                {titulo}
            </div>
            <div className='container-form'>
                <div className='label-form'>Usuario: </div>
                <div><input defaultValue={user.userName} className='label-form-data' disabled={(data !== undefined) ? true : false} onChange={data => setUser({ ...user, userName: data.target.value })}></input></div>
            </div>
            <div className='container-form'>
                <div className='label-form'>Nombre: </div>
                <div><input defaultValue={user.fullName} className='label-form-data' onChange={data => setUser({ ...user, fullName: data.target.value })} ></input></div>
            </div>
            <div className='container-form'>
                <div className='label-form'>Nombre abreviado: </div>
                <div><input defaultValue={user.shortName} maxLength={15} className='label-form-data' onChange={data => setUser({ ...user, shortName: data.target.value })} ></input></div>
            </div>
            <div className='container-form'>
                <div className='label-form'>Email: </div>
                <div><input defaultValue={user.email} className='label-form-data' onChange={data => setUser({ ...user, email: data.target.value })}></input></div>
            </div>
            <div className='container-form'>
                <div className='label-form'>Nueva Contraseña: </div>
                <div style={{display: 'flex', alignItems: 'center'}}>
                    <input type={showPassword ? 'text' : 'password'} value={user.password} style={{width:'50%'}} className='label-form-data' onChange={data => setUser({ ...user, password: data.target.value })}></input>
                    
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} onClick={() => setShowPassword(!showPassword)} style={{width:'3%', cursor: 'pointer', marginLeft: '8px', color: 'gray'}}/>
                    
                    <button type="button" onClick={generarContrasena} style={{width: '35%', marginLeft: '8px', padding: '4px 10px', fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap', backgroundColor: COLOR_BOTONES, color: 'white', border: 'none', borderRadius: '4px'}}>Generar contraseña</button>
                </div>
            </div>
            <div className='container-form'>
                <div className='label-form'>Habilitado: </div>
                { !controlUsuarios &&
                    <div><input type="checkbox" defaultChecked={user.enabled} className='label-form-check-data' onChange={data => setUser({ ...user, enabled: data.target.checked })}></input></div>
                }
                { controlUsuarios &&
                    <div><input type="checkbox" className='label-form-check-data' disabled ></input></div>
                }

            </div>

            <div className='container-form'>
                <div className='label-form'>Rol: </div>
                <div className='container-checkbox'>      
                <Select
                    styles={{
                        multiValueLabel: (base) => ({
                        ...base,
                        backgroundColor: "#00D4E2",
                        color: 'white',
                        borderRadius: "none",
                        fontSize: "1rem"
                        }), 
                        multiValueRemove: (base) => ({
                            ...base,
                            backgroundColor: "#00D4E2",
                            color: 'white',
                            borderRadius: "none",
                            fontSize: "1rem"
                        }),
                    }}
                    isMulti
                    placeholder=""
                    options={items && items.length > 0 ? items.map(function(item) {
                        return { value: item.typeName, label: item.descr };
                      }) : []}
                    value={selectedOptions}
                    onChange={handleSelectChange}
                />
                </div>
            </div>
            {esSocio && <div className='container-form'>
                <div className='label-form'>Nro.Socio: </div>
                <div><input defaultValue={user.nroSocio} className='label-form-data' onChange={data => setUser({ ...user, nroSocio: data.target.value })}></input></div>
            </div>}
            {   //Se decide no mostrar. Queda la condicion de renderizado en falso por si se solicita en un futuro mostrarlo nuevamente.
                false && <div className='container-form'>
                    <div className='label-form'>Visualiza de: </div>
                    <div className='container-checkbox'>      
                    <Select
                        styles={{
                            multiValueLabel: (base) => ({
                            ...base,
                            backgroundColor: "#00D4E2",
                            color: 'white',
                            borderRadius: "none",
                            fontSize: "1rem"
                            }), 
                            multiValueRemove: (base) => ({
                                ...base,
                                backgroundColor: "#00D4E2",
                                color: 'white',
                                borderRadius: "none",
                                fontSize: "1rem"
                            }),
                        }}
                        isMulti
                        options={(listGroups && listGroups.length > 0) && listGroups.map((item) => ({value: item._id, label: item.name}))}
                        placeholder=""
                        value={selectedGroupOptions && selectedGroupOptions.length > 0 && selectedGroupOptions.map(function(item) {
                        
                            return {value: item._id, label: item.name};
                        })}
                        onChange={handleSelectGroupChange}
                    />
                    </div>
                </div>
            }
            <div className='container-form'>
                <div className='label-form'>Pertenece a: </div>
                <div className='container-list-Wgroups'>  
                    <Select
                        styles={{
                            multiValueLabel: (base) => ({
                            ...base,
                            backgroundColor: "#00D4E2",
                            color: 'white',
                            borderRadius: "none",
                            fontSize: "1rem"
                            }), 
                            multiValueRemove: (base) => ({
                                ...base,
                                display: 'none'
                            }),
                            dropdownIndicator: (base) => ({
                                ...base,
                                display: 'none'
                            }),
                            indicatorsContainer: (base) => ({
                                ...base,
                                display: 'none'
                            }),
                        }}
                        isMulti
                        menuIsOpen={false}
                        isSearchable={false} 
                        placeholder=""
                        value={groups && groups.length > 0 && groups.map(function(item) {
                            return {value: item._id, label: item.name};
                        })}
                        disabled
                    />
                </div>
            </div>

            {showActivosAllowedFilter && <div className='container-form activosAllowedConfigDiv'>
                <div className='label-form'>Activos que visualiza: </div>
                <div className='optionsSelectActivosAllowedDiv'>
                    <div className='getActivosFromUserContainer'>
                        <label style={{fontWeight:600}}>Recuperar desde otro Usuario</label>
                        <Select styles={{
                            input: (base) => ({
                                ...base,
                                width: '100%'
                            }),
                            container: (base) => ({
                                ...base,
                                width: '75%', // or '75%', or any custom value
                            }),
                        }} placeholder="" options={userOptions} value={{}} onChange={handleGetActivosAllowedFromUser}/>
                    </div>
                    <div className="facilitatorGetByNivel1Div">
                        <label>Recuperar desde <DictionaryText text={"Nivel 1"}/></label>
                        <Select 
                            styles={{
                                container: base => ({
                                    ...base,
                                    width:'75%',
                                }),
                            }}
                            value={{}}
                            options={areasOptions}
                            onChange={(value)=>handleChangeFacilitatorNivel1(value)}
                        />
                    </div>
                </div>

                    <Select
                        styles={{
                            multiValueLabel: (base) => ({
                            ...base,
                            backgroundColor: "#00D4E2",
                            color: 'white',
                            borderRadius: "none",
                            fontSize: "1rem"
                            }), 
                            container: (base) => ({
                                ...base,
                                width:'100%'
                            }),
                            multiValueRemove: (base) => ({
                                ...base,
                                backgroundColor: "#00D4E2",
                                color: 'white',
                                borderRadius: "none",
                                fontSize: "1rem"
                            }),
                        }}
                        isMulti
                        placeholder=""
                        options={activoList ? activoList.map(a => ({label:a.name, value: a.codActivo})) : []}
                        value={selectedActivosOptions}
                        onChange={handleChangeActivosAllowed}
                    />
            </div>}
            {showActivosAllowedFilter && <div className='container-form activosAllowedConfigDiv'>
                <div className='label-form'>Checklists que visualiza: </div>
                <div className='optionsSelectActivosAllowedDiv'>
                    <div className='getActivosFromUserContainer'>
                        <label style={{fontWeight:600}}>Recuperar desde otro Usuario</label>
                        <Select styles={{
                            input: (base) => ({
                                ...base,
                                width: '100%'
                            }),
                            container: (base) => ({
                                ...base,
                                width: '75%', // or '75%', or any custom value
                            }),
                        }} placeholder="" options={userOptions} value={{}} onChange={handleGetChecklistsAllowedFromUser}/>
                    </div>
                </div>

                    <Select
                        styles={{
                            multiValueLabel: (base) => ({
                            ...base,
                            backgroundColor: "#00D4E2",
                            color: 'white',
                            borderRadius: "none",
                            fontSize: "1rem"
                            }), 
                            container: (base) => ({
                                ...base,
                                width:'100%'
                            }),
                            multiValueRemove: (base) => ({
                                ...base,
                                backgroundColor: "#00D4E2",
                                color: 'white',
                                borderRadius: "none",
                                fontSize: "1rem"
                            }),
                        }}
                        isMulti
                        placeholder=""
                        options={checklistsOptions}
                        value={selectedChecklistsOptions}
                        onChange={handleChangeChecklistsAllowed}
                    />
            </div>}

            <div className='extraOptionsContainer'>
                <h5>En primer LogIn</h5>
                <div className='optionsContainer'>
                    <div className='checkGroup'>
                        <label>Mostrar Introducción</label>
                        {/* <input type='checkbox' className="extraOptionCheckInput" checked={user.showTuto} name='showTuto' onChange={(e) => handleChangeOptions(e)}/> */}
                        <input type='checkbox' className="extraOptionCheckInput" checked={false} disabled={true}  name='showTuto' onChange={(e) => handleChangeOptions(e)}/>
                    </div>
                    <div className='checkGroup'>
                        <label>Forzar cambio de contraseña</label>
                        {/* <input type='checkbox' className="extraOptionCheckInput" checked={user.mustChangePassword} name='mustChangePassword' onChange={(e) => handleChangeOptions(e)}/> */}
                        <input type='checkbox' className="extraOptionCheckInput" checked={false} disabled={true} name='mustChangePassword' onChange={(e) => handleChangeOptions(e)}/>
                    </div>
                </div>
            </div>


            <div style={{display:'flex'}}>
                <div className='container-btn' >
                    <button className='btn-form' style={{backgroundColor: COLOR_BOTONES}} onClick={handlerGenerar}>{tituloBoton}</button>
                </div>
                <div className='container-btn'>
                    <button className='btn-cancel-form' onClick={minimizar}>Cancelar</button>
                </div>
            </div>
        </div>
    );
}

export default FormUser